import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  confirmArrival,
  decideLoad,
  saveLab,
  saveSampling,
  saveScale,
} from "@/features/intake/api/intake.api"
import type {
  ConfirmArrivalPayload,
  DecisionPayload,
  SaveLabPayload,
  SaveSamplingPayload,
  SaveScalePayload,
} from "@/features/intake/api/types"
import { intakeKeys } from "@/features/intake/hooks/use-intake"
import { extractApiErrorMessage } from "@/lib/api/client"

function useIntakeInvalidator() {
  const queryClient = useQueryClient()
  return (loadId?: string | number) => {
    queryClient.invalidateQueries({ queryKey: intakeKeys.all })
    if (loadId) {
      queryClient.invalidateQueries({ queryKey: intakeKeys.detail(loadId) })
    }
  }
}

export function useConfirmArrival() {
  const { t } = useTranslation("intake")
  const invalidate = useIntakeInvalidator()
  return useMutation({
    mutationFn: (payload: ConfirmArrivalPayload) => confirmArrival(payload),
    onSuccess: () => {
      toast.success(t("toast.arrivalConfirmed"))
      invalidate()
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

export function useSaveScale(loadId: string | number) {
  const { t } = useTranslation("intake")
  const invalidate = useIntakeInvalidator()
  return useMutation({
    mutationFn: (payload: SaveScalePayload) => saveScale(loadId, payload),
    onSuccess: () => {
      toast.success(t("toast.scaleSaved"))
      invalidate(loadId)
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

export function useSaveSampling(loadId: string | number) {
  const { t } = useTranslation("intake")
  const invalidate = useIntakeInvalidator()
  return useMutation({
    mutationFn: (payload: SaveSamplingPayload) => saveSampling(loadId, payload),
    onSuccess: () => {
      toast.success(t("toast.samplingSaved"))
      invalidate(loadId)
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

export function useSaveLab(loadId: string | number) {
  const { t } = useTranslation("intake")
  const invalidate = useIntakeInvalidator()
  return useMutation({
    mutationFn: (payload: SaveLabPayload) => saveLab(loadId, payload),
    onSuccess: () => {
      toast.success(t("toast.labSaved"))
      invalidate(loadId)
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

export function useDecideLoad(loadId: string | number) {
  const { t } = useTranslation("intake")
  const invalidate = useIntakeInvalidator()
  return useMutation({
    mutationFn: (payload: DecisionPayload) => decideLoad(loadId, payload),
    onSuccess: (_data, variables) => {
      toast.success(
        variables.decision === "ACCEPTED"
          ? t("toast.loadAccepted")
          : t("toast.loadRejected"),
      )
      invalidate(loadId)
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}
